import React from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
} from '@mui/material';
import { LightUserDto } from '@cloudcost/shared-types/users';
import { useSystemValidation } from '../hooks/useSystemValidation';
import { SystemFormData } from '../../types';

interface Department {
  id: number;
  name: string;
}

interface CreateSystemModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: SystemFormData) => void;
  users: LightUserDto[];
  departments: Department[];
  formData: SystemFormData;
  setFormData: (data: SystemFormData) => void;
}

export const CreateSystemModal: React.FC<CreateSystemModalProps> = ({
  open,
  onClose,
  onSubmit,
  users,
  departments,
  formData,
  setFormData,
}) => {
  const { validationErrors, validateForm, clearFieldError } = useSystemValidation();

  const handleSubmit = () => {
    if (validateForm(formData)) {
      onSubmit(formData);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{
        backdrop: Backdrop,
      }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}>
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: '0 12px 40px rgba(16,35,58,.16)',
            p: 4,
            borderRadius: '14px',
          }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            Create System
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              id="system-name"
              label="System Name"
              variant="outlined"
              fullWidth
              value={formData.name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  name: e.target.value,
                });
                clearFieldError('name');
              }}
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              inputProps={{ 'data-testid': 'system-creation-name' }}
            />

            <Autocomplete
              disablePortal
              id="combo-box-departments"
              options={departments}
              value={
                departments.find(
                  (row: Department) => row.id === formData.department_id,
                ) ?? null
              }
              getOptionLabel={(option: Department) => option.name}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department"
                  variant="outlined"
                  error={!!validationErrors.department_id}
                  helperText={validationErrors.department_id}
                  inputProps={{ ...params.inputProps, 'data-testid': 'system-creation-department' }}
                />
              )}
              onChange={(event, value: Department | null) => {
                if (value) {
                  setFormData({
                    ...formData,
                    department_id: value.id,
                  });
                  clearFieldError('department_id');
                }
              }}
            />

            <Autocomplete
              disablePortal
              id="combo-box-users"
              options={users}
              value={
                users.find(
                  (row: LightUserDto) => row.id === formData.systemowner,
                ) ?? null
              }
              getOptionLabel={(option: LightUserDto) => option.username}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pick System Owner"
                  variant="outlined"
                  error={!!validationErrors.systemowner}
                  helperText={validationErrors.systemowner}
                  inputProps={{ ...params.inputProps, 'data-testid': 'system-creation-owner' }}
                />
              )}
              onChange={(event, value: LightUserDto | null) => {
                if (value) {
                  setFormData({
                    ...formData,
                    systemowner: value.id,
                  });
                  clearFieldError('systemowner');
                }
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                color="primary"
                fullWidth
                data-testid="system-creation-submit">
                Create System
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};